import * as React from 'react';
import {
    Icon, Button, Message, SemanticICONS,
} from 'semantic-ui-react';
import DOMPurify from 'dompurify';

import { tl } from 'common/services/i18nService';
import * as styles from 'common/components/notifications/NotificationDisplay.scss';
import { removeAlert } from 'common/components/notifications/actions';
import { AlertLevel, LinkAlert } from 'common/components/notifications/types';

import { IModuleStore } from 'redux-dynamic-modules';

interface NotificationDisplayProps {
    appStore: IModuleStore<any>;
    alertTitleCaption?: string;
    alertMessageCaption: string;
    alertMessageInstructions?: string[];
    level: AlertLevel;
    payload?: any;
    link?: LinkAlert;
    icon?: SemanticICONS;
    loading?: boolean;
    manipulatePayload?: (payload: any) => any;
}

function NotificationDisplay(props: NotificationDisplayProps) {
    const {
        appStore, alertTitleCaption, alertMessageCaption, alertMessageInstructions, level, payload, link, icon, loading, manipulatePayload,
    } = props;

    const performManipulatePayload = (payload: any) => {
        if (manipulatePayload) {
            return manipulatePayload(payload);
        }
        return payload;
    };
    const [notificationPayload, setNotificationPayload] = React.useState(performManipulatePayload(payload));

    React.useEffect(() => {
        setNotificationPayload(performManipulatePayload(payload));
    }, [payload]);

    const alertType: any = {};
    if (level) {
        alertType[level] = true;
    }
    const dismissNotification = (): void => {
        appStore.dispatch(removeAlert({
            level,
            info: alertMessageCaption,
            key: alertTitleCaption,
        }));
    };

    const handleClick = (): void => {
        appStore.dispatch(link.action);
        dismissNotification();
    };

    const className = notificationPayload?.showInBottom ? styles.bottomPositionalertsBox : styles.alertsBox;
    const dragItem = notificationPayload?.showInBottom ? notificationPayload?.dragItem : undefined;
    return (
        <div className={className}>
            <div className={styles.notificationContainer}>
                <Message
                    icon
                    {...alertType}
                    onDismiss={dismissNotification}
                >
                    <Icon name={icon || 'info circle'} className={loading ? '' : styles.normalSizeIcon} loading={loading} />
                    <Message.Content>
                        {alertTitleCaption && (
                            <Message.Header className={styles.header}>
                                {tl(`${alertTitleCaption}`, payload)}
                                &nbsp;
                                {dragItem ? dragItem?.title : ''}
                            </Message.Header>
                        )}
                        {alertMessageCaption && (
                            <div
                                dangerouslySetInnerHTML={
                                    { __html: DOMPurify.sanitize(tl(`${alertMessageCaption}`, notificationPayload)) }
                                }
                            />
                        )}
                        {alertMessageInstructions?.length > 0 && (
                            <Message.List>
                                {alertMessageInstructions.map((instruction: string, index: number) => (
                                    <Message.Item key={`${instruction}_${index}`}>{instruction}</Message.Item>
                                ))}
                            </Message.List>
                        )}
                        {link && link.text && link.url ? (
                            <>
                                <a href={link.url} target="_blank" className={styles.link}>
                                    {link.text}
                                </a>
                                {link.nonLinkText && <span>{link.nonLinkText}</span>}
                            </>
                        ) : null}
                        {link && link.text && link.action ? (
                            <Button
                                compact
                                onClick={handleClick}
                                className={styles.button}
                            >
                                {link.text}
                            </Button>
                        ) : null}
                    </Message.Content>
                </Message>
            </div>
        </div>
    );
}

export default NotificationDisplay;
